import React, { useContext, useEffect, useRef, useState } from 'react';

import { PHRPagination } from '@palmhr/palmhr-ui-lib';
import { useQueryClient } from '@tanstack/react-query';
import _ from 'lodash';

import IconButton from 'components/IconButton';
import Search from 'components/Search';
import SearchFilterMenu from 'components/searchFilterMenu/SearchFilterMenu';
import SideTransitionContainer from 'components/searchFilterMenu/SideTransitionContainer';
import { initialfilterMapArray } from 'Insights/Subscriptions/constants';
import { SubscriptionsContext } from 'Insights/Subscriptions/SubscriptionsContext';
import { useQueryGetSubscriptionsListByMonth, queryKeys } from 'Insights/useInsights';
import useDebounce from 'Shared/useDebounce';
import { useQueryGetAdminUsers } from 'Users/hooks/useUsers';

import styles from './SubscriptionsView.module.scss';
import SubscriptionsTable from '../SubscriptionsTable/SubscriptionsTable';

const SubscriptionsView = ({ handleShowDetailsModal, setSelected }) => {
  const { data: users } = useQueryGetAdminUsers({ currentPage: 1, perPage: 9999 });

  const { selectedMonth } = useContext(SubscriptionsContext);
  const queryClient = useQueryClient();
  const isAsc = useRef(false);
  const tableRef = useRef(null);
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearch = useDebounce(searchValue, 500);
  const [filterMenu, setFilterMenu] = useState(false);
  const [filterMapArray, setFilterMapArray] = useState(initialfilterMapArray);
  const [selectedFilter, setSelectedFilter] = useState([]);

  const [params, setParams] = useState({
    currentPage: 1,
    perPage: 10,
    search: '',
    sort: '',
    date: selectedMonth,
  });

  useEffect(() => {
    setParams((prevState) => ({ ...prevState, currentPage: 1, search: debouncedSearch }));
  }, [debouncedSearch]);

  useEffect(() => {
    setParams((prevState) => ({ ...prevState, currentPage: 1, date: selectedMonth }));
  }, [selectedMonth]);

  const { isLoading, data } = useQueryGetSubscriptionsListByMonth(params);

  const subscriptions = data?.data.items || [];
  const subscriptionsPagination = data?.data?.pagination;

  const updateList = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.insightCustomers(params) });
  };

  const onSort = (option) => {
    isAsc.current = !isAsc.current;
    const dir = isAsc.current ? '+' : '-';
    setParams((prevState) => ({ ...prevState, currentPage: 1, sort: `${dir}${option}` }));
  };

  const updatePaginationParams = ({ currentPage, perPage }) =>
    setParams((prevState) => ({ ...prevState, currentPage, perPage }));

  const closeFilterMenu = () => {
    setFilterMenu(false);
  };

  const openFilterMenu = () => {
    setFilterMenu(true);
  };

  const onSelectField = (value, field, id, groupTitle, label) => {
    setSelectedFilter((prevFilters) => {
      const updatedFilters = _.cloneDeep(prevFilters);
      const filterIndex = updatedFilters.findIndex((f) => f.id === id);

      if (filterIndex >= 0) {
        updatedFilters[filterIndex].selected = value;
      } else {
        updatedFilters.push({ field, id, label, selected: value, title: groupTitle });
      }

      return updatedFilters;
    });

    const stateArray = [...filterMapArray];
    stateArray.every((el) => {
      if (el.title === groupTitle) {
        el.options.every((elem) => {
          if (`${elem.id}` === `${id}`) {
            elem.selected = value;
            return false;
          }
          return true;
        });
      }
      return true;
    });
    setFilterMapArray(stateArray);
  };

  const onApplyFilter = (filters) => {
    setFilterMenu(false);
    const updatedParams = { ...params };

    filters.forEach(({ title }) => {
      const categoryKey = _.camelCase(title);
      const selectedFilters = filters
        .filter((f) => f.title.toLowerCase() === title.toLowerCase() && f.selected)
        .map((f) => f.field);

      if (selectedFilters.length > 0) {
        updatedParams[categoryKey] = selectedFilters.join(',');
      } else {
        delete updatedParams[categoryKey];
      }
    });

    setParams(updatedParams);

    // Clear ALL
    if (filters.length === 0) {
      const stateArray = [...filterMapArray];
      stateArray.forEach((el) => {
        el.options.forEach((elem) => {
          elem.selected = false;
        });
      });

      setFilterMapArray(stateArray);
      setSelectedFilter([]);
    }
  };

  const xScroll = (direction) => {
    if (!tableRef || !tableRef.current) return;
    tableRef.current.scroll({
      left:
        direction === 'left'
          ? tableRef.current.scrollLeft - tableRef.current.clientWidth + 410
          : tableRef.current.scrollLeft + tableRef.current.clientWidth - 410,
      behavior: 'smooth',
    });
  };

  return (
    <div>
      <section className='d-flex justify-content-between align-items-center mt-4 mb-3 mx-5'>
        <Search
          value={searchValue}
          placeholder='Search by customer, account, subscription'
          onChange={(e) => setSearchValue(e.target.value)}
          onClear={() => setSearchValue('')}
        />
        <div className='d-flex align-items-center'>
          <SideTransitionContainer isIn={filterMenu} zIndex={105} onClose={closeFilterMenu}>
            <SearchFilterMenu
              filterMapArray={filterMapArray}
              selectedFilter={selectedFilter}
              onApplyFilter={onApplyFilter}
              onSelectField={onSelectField}
              onClose={closeFilterMenu}
            />
          </SideTransitionContainer>
          <IconButton icon='icon-filter' onClick={openFilterMenu} size='md' />
          <div className='d-flex align-items-center'>
            <IconButton
              icon='icon-chevron-left'
              size='md'
              className='me-2'
              isDisabled={isLoading}
              onClick={() => xScroll('left')}
            />

            <IconButton icon='icon-chevron-right' size='md' isDisabled={isLoading} onClick={() => xScroll('right')} />
          </div>
        </div>
      </section>

      <div className='mx-5'>
        <div
          className={styles.container}
          style={{ height: 'calc(100vh - 335px)', overflowX: 'auto', whiteSpace: 'nowrap' }}
          ref={tableRef}
        >
          <SubscriptionsTable
            onSort={onSort}
            users={users}
            isLoading={isLoading}
            subscriptions={subscriptions}
            updateList={updateList}
            onSelectRow={(item) => {
              setSelected(item);
              if (item.accountStatus !== 'PENDING') {
                handleShowDetailsModal();
              }
            }}
          />
        </div>
        {subscriptionsPagination?.total > 0 && (
          <PHRPagination onPaginationAction={updatePaginationParams} pagination={subscriptionsPagination} />
        )}
      </div>
    </div>
  );
};

export default SubscriptionsView;
